import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { getTenantId, getDepartmentId } from 'src/app/services/store-snapshot.service';
import { IdName } from 'src/app/shared/models/id-name';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import {
  ICertificateRenew,
  ICertificate,
  ICertificateGroup,
  IApiResponseGroupCertificates,
  ICertificateLog,
} from '../models/certificate.model';
import { environment } from '../../../environments/environment';
import { FormDataService } from '../../shared/attachments/service/form-data.service';

const COLUMN_TO_KEY_MAP: any = {
  type: 'CertificateType',
  issueDate: 'IssueDate',
  expiryDate: 'ExpirationDate',
  accessPositions: 'PositionsWithAccess.Name',
  accessPositionGroups: 'PositionGroupsWithAccess.Name',
  accessRoles: 'RolesWithAccess.Name',
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  accessPositions: 'access',
  accessPositionGroups: 'access',
  accessRoles: 'access',
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
};

@Injectable({
  providedIn: 'root',
})
export class CertificateApiService {
  private headers = { 'content-type': 'application/json' };
  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/certificates`;
  }

  private get certificatesForIOUrl(): string {
    return `${this.url}/Interconnected`;
  }

  constructor(private http: HttpClient) {}

  getCertificatesByMenuWithFiltering(
    currentFilter?: MainTableFilterModel
  ): Observable<IApiResponseGroupCertificates> {
    const url = `${this.url}/byMenu`;
    let params = createFilterParams(currentFilter);

    if (currentFilter?.additional?.menuId) {
      params = params.set('menuId', currentFilter.additional?.menuId);
    }

    if (currentFilter?.additional?.showDeleted) {
      params = params.set('IsDeleted', 'true');
    }

    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );

    return this.http.get<IApiResponseGroupCertificates>(url, { params });
  }

  getAllCertificatesGrouped(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<ICertificateGroup[]>> {
    const url = `${this.url}/groups`;
    const params = createFilterParams(currentFilter);

    return this.http.get<ApiResponse<ICertificateGroup[]>>(url, { params });
  }

  getSpecificCertificate(certificateId: string): Observable<ICertificate> {
    const url = `${this.url}/${certificateId}`;
    return this.http.get<ICertificate>(url);
  }

  loadCertificateLog(
    certificateId: string,
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<ICertificateLog[]>> {
    const params = createFilterParams(currentFilter);

    return this.http.get<ApiResponse<ICertificateLog[]>>(`${this.url}/${certificateId}/log`, {
      params,
    });
  }

  loadCertificatesForIO(): Observable<IdName[]> {
    return this.http.get<IdName[]>(this.certificatesForIOUrl);
  }

  createCertificate(certificate: ICertificate): Observable<{ id: string }> {
    const formData = FormDataService.mapToFormData(certificate);
    return this.http.post<{ id: string }>(this.url, formData);
  }

  updateCertificate(certificate: ICertificate): Observable<void> {
    const formData = FormDataService.mapToFormData(certificate);
    const url = `${this.url}/${certificate.id}`;
    return this.http.put<void>(url, formData);
  }

  deleteCertificate(id: string): Observable<void> {
    const url = `${this.url}/${id}`;
    return this.http.delete<void>(url);
  }

  renewCertificate(certificate: ICertificateRenew): Observable<void> {
    const formData = FormDataService.mapToFormData(certificate);
    const url = `${this.url}/${certificate.id}/renew`;
    return this.http.put<void>(url, formData);
  }

  restoreCertificate(id: string): Observable<void> {
    return this.http.put<void>(`${this.url}/${id}/restore`, {});
  }
}
